import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { Zap, Play, CheckCircle2, Flame } from "lucide-react";
import { WHATSAPP_CONFIG, HOTMART_CONFIG, PRICING } from "@/config/constants";

type Product = {
  id: string;
  title: string;
  subtitle?: string;
  priceOriginal?: string;
  price?: string;
  priceInstallmentMain?: string;
  priceInstallmentSuffix?: string;
  cta?: string;
  href?: string;
  isMentoria?: boolean;
};

const WHATSAPP_URL = `https://api.whatsapp.com/send?phone=${WHATSAPP_CONFIG.supportNumber}`;

const PRODUCTS: Product[] = [
  {
    id: "jornada-8-pilares",
    title: PRICING.jornada8Pilares.name,
    subtitle: PRICING.jornada8Pilares.description,
    priceOriginal: PRICING.jornada8Pilares.priceOriginal,
    priceInstallmentMain: PRICING.jornada8Pilares.priceInstallment,
    priceInstallmentSuffix: `em ${PRICING.jornada8Pilares.installments}`,
    price: `${PRICING.jornada8Pilares.price}`,
    cta: "QUERO ME LIBERTAR AGORA",
    href: HOTMART_CONFIG.jornada8Pilares,
  },
];

export default function FinalCTA() {
  return (
    <section id="final-cta" className="relative pb-20 md:pb-24">
      {/* BG com grade + glow mais forte */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-primary/15 via-background to-background" />
        <div
          className="absolute inset-0 opacity-[0.08] [mask-image:radial-gradient(70%_70%_at_50%_20%,black,transparent)]"
          style={{
            backgroundImage:
              "linear-gradient(to right, hsl(var(--border)) 1px, transparent 1px), linear-gradient(to bottom, hsl(var(--border)) 1px, transparent 1px)",
            backgroundSize: "32px 32px",
          }}
        />
        <div className="absolute -top-32 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-primary/25 blur-3xl opacity-70" />
      </div>

      <div className="container">
        {/* Cabeçalho forte de decisão */}
        <div className="mx-auto max-w-3xl text-center mb-12 md:mb-16">
          <h2 className="text-4xl md:text-5xl font-bold bg-gradient-accent bg-clip-text text-transparent drop-shadow-[0_0_18px_rgba(100,192,242,0.6)]">
            Decida mudar hoje!
          </h2>

          <p className="mt-4 text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Um passo agora pode ser o divisor de águas entre continuar preso
            no ciclo das apostas ou finalmente retomar o controle da sua vida.
          </p>

          <p className="mt-2 text-xs md:text-sm text-muted-foreground/80">
            A mesma metodologia que já ajudou{" "}
            <span className="font-semibold text-foreground">milhares de pessoas</span>{" "}
            a reconstruírem sua relação com o dinheiro e com a própria história.
          </p>
        </div>

        {/* Grid responsivo */}
        <div className="flex justify-center">
          <div className="grid gap-6 md:gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 max-w-4xl justify-items-center">
            {PRODUCTS.map((p, idx) => {
              const isFeatured = idx === 0;

              return (
                <Card
                  key={p.id}
                  className={[
                    "relative flex flex-col h-full w-full max-w-lg overflow-hidden border bg-gradient-to-b from-background/90 via-background/95 to-background/98 backdrop-blur supports-[backdrop-filter]:bg-background/60 transition-all duration-300",
                    isFeatured
                      ? "border-primary/60 shadow-[0_18px_60px_rgba(0,0,0,0.55)] ring-2 ring-primary/60 md:scale-[1.03]"
                      : "border-border/60 hover:shadow-lg",
                  ].join(" ")}
                >
                  {/* Glow interno */}
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0 opacity-80"
                    style={{
                      background:
                        "radial-gradient(800px 260px at 50% 0%, hsl(var(--primary)/0.18), transparent 70%)",
                    }}
                  />

                  {/* Faixa “mais vendido” */}
                  <div className="relative flex justify-center mt-4">
                    <div className="inline-flex items-center gap-2 rounded-full bg-emerald-500/10 border border-emerald-500/50 px-3 py-1 text-[11px] font-semibold text-emerald-300 shadow-sm shadow-emerald-500/40">
                      <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      Mais vendido
                    </div>
                  </div>

                  <CardHeader className="relative flex flex-col items-center gap-1 pt-6 pb-4 text-center">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">
                      Inscreva-se agora
                    </span>
                    <h3 className="mt-1 text-lg md:text-xl font-bold leading-snug">
                      {p.title}
                    </h3>
                    {p.subtitle && (
                      <p className="mt-2 text-xs md:text-sm text-muted-foreground max-w-xs">
                        {p.subtitle}
                      </p>
                    )}
                  </CardHeader>

                  <CardContent className="relative flex flex-col items-center text-center pb-4 px-6">
                    {/* Linha DE: preço antigo */}
                    {p.priceOriginal && (
                      <div className="flex flex-col items-center gap-2">
                        <div className="text-lg md:text-2xl font-bold text-muted-foreground flex flex-col items-center gap-1">
                          <div>
                            <span className="mr-2 opacity-70">De</span>
                            <span className="line-through text-red-400">{p.priceOriginal}</span>
                          </div>
                        </div>
                        <span className="inline-flex items-center gap-1 rounded-full bg-yellow-500/20 px-3 py-1 text-xs md:text-sm font-bold uppercase tracking-[0.22em] text-yellow-400">
                          Desconto especial de lançamento
                        </span>
                      </div>
                    )}

                    {/* Preço à vista em destaque */}
                    {p.price && (
                      <div className="mb-4 text-center gap-0 mt-10">
                        <span className="text-[11px] font-semibold text-muted-foreground tracking-[0.22em] uppercase leading-none block mb-2">
                          Por apenas
                        </span>
                        <div className="text-5xl md:text-6xl font-black text-white mb-3">
                          {p.price}
                        </div>
                        <div className="text-xs md:text-sm font-semibold text-emerald-300 bg-emerald-500/20 border border-emerald-500/60 rounded-full px-4 py-1 inline-block">
                          no Pix com desconto
                        </div>
                      </div>
                    )}

                    {/* Ou parcelado */}
                    {p.priceInstallmentMain && (
                      <div className="flex flex-col items-center gap-1 mb-8">
                        <span className="text-[10px] font-semibold text-muted-foreground tracking-[0.18em] uppercase leading-tight">
                          Ou parcelado em
                        </span>
                        <div className="flex flex-wrap items-baseline justify-center gap-1 -mt-0.5">
                          <span className="text-sm md:text-base font-bold leading-none">
                            {p.priceInstallmentMain}
                          </span>
                          {p.priceInstallmentSuffix && (
                            <span className="text-[9px] md:text-[10px] text-muted-foreground/80">
                              {p.priceInstallmentSuffix} no cartão
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Benefícios rápidos */}
                    <div className="mt-3 w-full text-left text-xs md:text-sm text-muted-foreground">
                      <p className="mb-2 font-semibold text-foreground">
                        Ao entrar hoje, você garante:
                      </p>
                      <ul className="space-y-1.5">
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="mt-[2px] min-h-4 min-w-4 h-4 w-4 text-emerald-400" />
                          <span>Diminuição imediata do desejo de apostar.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="mt-[2px] min-h-4 min-w-4 h-4 w-4 text-emerald-400" />
                          <span>
                            Reprogramação mental com técnicas de neurociência.
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="mt-[2px] min-h-4 min-w-4 h-4 w-4 text-emerald-400" />
                          <span>
                            Primeiro passo real rumo à liberdade das apostas.
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="mt-[2px] min-h-4 min-w-4 h-4 w-4 text-emerald-400" />
                          <span>
                            Imersão completa nos 8 pilares da libertação
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="mt-[2px] min-h-4 min-w-4 h-4 w-4 text-emerald-400" />
                          <span>
                            Mais de 38 exercícios práticos para fortalecer sua mente
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="mt-[2px] min-h-4 min-w-4 h-4 w-4 text-emerald-400" />
                          <span>
                            Transformação duradoura e proteção definitiva contra recaídas
                          </span>
                        </li>
                      </ul>
                    </div>
                  </CardContent>

                  <CardFooter className="relative flex flex-col items-center gap-3 pb-6 px-6">
                    {/* CTA principal */}
                    {p.href ? (
                      <Button
                        size="lg"
                        variant="finalcta"
                        className={[
                          "w-full max-w-[100%] h-12 text-sm md:text-base font-bold shadow-lg shadow-green-500/50 hover:shadow-green-400/70 hover:-translate-y-[1.5px] transition-all",
                          "relative overflow-hidden bg-gradient-to-r from-green-400 to-emerald-500 hover:from-green-300 hover:to-emerald-400",
                        ].join(" ")}
                        asChild
                      >
                        <a
                          href={p.href}
                          target={p.isMentoria ? "_blank" : "_blank"}
                          rel="noopener noreferrer"
                          aria-label={`${p.title} - ${p.cta ?? "Saiba mais"}`}
                        >
                          <span className="absolute inset-0 bg-gradient-to-r from-green-300/30 via-green-300/0 to-green-300/30 opacity-0 group-hover:opacity-100 pointer-events-none" />
                          <Play className="mr-2 h-4 w-4" />
                          {p.cta ?? "Comece agora"}
                        </a>
                      </Button>
                    ) : (
                      <Button
                        size="lg"
                        className="w-full max-w-[90%] h-11 text-sm md:text-base font-bold shadow-lg shadow-green-500/50 hover:shadow-green-400/70 hover:-translate-y-[1.5px] transition-all bg-gradient-to-r from-green-400 to-emerald-500 hover:from-green-300 hover:to-emerald-400"
                        onClick={() => {
                          if ((window as any).fbq) {
                            (window as any).fbq("track", "AddToCart", {
                              content_name: "LDA " + p.id,
                              currency: "BRL",
                            });
                          }
                          window.open(WHATSAPP_URL, "_blank");
                        }}
                      >
                        <Play className="mr-2 h-4 w-4" />
                        {p.cta ?? "Comece agora"}
                      </Button>
                    )}

                    {/* Acesso + garantia */}
                    <div className="flex flex-col items-center gap-1 text-[11px] text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Zap className="h-3 w-3 text-primary" />
                        <span>Acesso imediato</span>
                      </div>
                    </div>

                    {/* Urgência discreta */}
                    <p className="mt-2 text-[10px] text-muted-foreground/80 text-center max-w-xs">
                      Se você sair da página, não há garantia de encontrar essa
                      mesma condição e bônus novamente.
                    </p>
                  </CardFooter>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
